import * as Sentry from "@sentry/react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { useEffect } from "react";

const DEFAULT_ENVIRONMENT = "development";
const DEFAULT_APP_NAME = typeof window !== "undefined" && window.location.pathname.startsWith("/portal") ? "portal" : "tenant";

let sentryEnabled = false;
let cachedEnvironment = DEFAULT_ENVIRONMENT;
let cachedRelease = undefined;

function normalizeEnvironment(value) {
  if (!value) return DEFAULT_ENVIRONMENT;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return DEFAULT_ENVIRONMENT;
  if (normalized === "prod") return "production";
  return normalized;
}

function resolveRelease() {
  return import.meta.env.VITE_APP_VERSION || import.meta.env.VITE_GIT_SHA || import.meta.env.VITE_COMMIT_SHA || import.meta.env.VITE_GIT_COMMIT_SHA || undefined;
}

function setGlobalTags({ environment, release, appName, region }) {
  Sentry.setTag("environment", environment);
  if (release) Sentry.setTag("version", release);
  if (region) Sentry.setTag("region", region);
  if (appName) {
    Sentry.setTag("app", appName);
    Sentry.setTag("app_name", appName);
  }
}

function isNoiseError(error) {
  const message = error?.message || error?.toString?.() || "";
  const lowered = String(message).toLowerCase();

  return (
    lowered.includes("resizeobserver") ||
    lowered.includes("failed to fetch") ||
    lowered.includes("networkerror") ||
    lowered.includes("load failed") ||
    lowered.includes("aborterror") ||
    lowered.includes("the operation was aborted") ||
    lowered.includes("chunkloaderror") ||
    lowered.includes("loading chunk")
  );
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = normalizeEnvironment(import.meta.env.VITE_SENTRY_ENV || import.meta.env.VITE_APP_ENV || import.meta.env.MODE);
  const release = resolveRelease();
  const region = import.meta.env.VITE_APP_REGION || import.meta.env.VITE_REGION || undefined;
  const appName = import.meta.env.VITE_APP_NAME || DEFAULT_APP_NAME;

  cachedEnvironment = environment;
  cachedRelease = release;

  // Don't initialize if no DSN is configured
  if (!dsn) {
    if (environment !== "production") {
      console.warn("⚠️ Sentry DSN not configured - error tracking disabled");
    }
    return;
  }

  try {
    const isProd = environment === "production";
    const integrations = [Sentry.dedupeIntegration()];

    integrations.push(
      Sentry.browserTracingIntegration({
        tracePropagationTargets: ["localhost", /^\//, /^https?:\/\/[^/]*cbcfx/i],
      }),
    );

    if (typeof Sentry.reactRouterV6BrowserTracingIntegration === "function") {
      integrations.push(
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      );
    }

    if (typeof Sentry.replayIntegration === "function") {
      integrations.push(
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
          mask: [
            "input[type='password']",
            "input[name*='token']",
            "input[name*='secret']",
            "input[name*='ssn']",
            "input[name*='iban']",
            "input[name*='account']",
            "input[name*='routing']",
            "[data-sentry-mask]",
          ],
        }),
      );
    }

    Sentry.init({
      dsn,
      environment,
      release,

      // Sample 100% of traces in dev, 10% in prod
      tracesSampleRate: isProd ? 0.1 : 1.0,

      // Capture 5% of sessions for replay in prod
      replaysSessionSampleRate: isProd ? 0.05 : 0,

      // Capture 80% of sessions with errors in prod
      replaysOnErrorSampleRate: isProd ? 0.8 : 1.0,

      integrations,

      ignoreErrors: ["ResizeObserver loop limit exceeded", "ResizeObserver loop completed with undelivered notifications", "ChunkLoadError", "Non-Error exception captured"],

      denyUrls: [/extensions\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-extension:\/\//i],

      // Filter out errors that aren't actionable
      beforeSend(event, hint) {
        const error = hint?.originalException;

        if (isNoiseError(error)) {
          return null;
        }

        if (event?.request?.headers) {
          const headers = { ...event.request.headers };
          delete headers.Authorization;
          delete headers.Cookie;
          delete headers.authorization;
          delete headers.cookie;
          event.request.headers = headers;
        }

        if (event?.user) {
          delete event.user.ip_address;
        }

        return event;
      },
    });

    sentryEnabled = true;

    setGlobalTags({ environment, release, region, appName });

    // Make Sentry available globally for ErrorBoundary
    if (typeof window !== "undefined") {
      window.Sentry = Sentry;
    }

    if (environment !== "production") {
      console.log("✅ Sentry initialized:", environment);
    }
  } catch (err) {
    if (environment !== "production") {
      console.warn("⚠️ Sentry failed to initialize:", err);
    }
  }
}

export function setSentryUserContext({ user, tenantId, tenantSlug, tenantName, memberRole, appName, clerkId, sessionId, region } = {}) {
  if (!sentryEnabled) return;

  const effectiveApp = appName || DEFAULT_APP_NAME;
  const resolvedRegion = region || import.meta.env.VITE_APP_REGION || import.meta.env.VITE_REGION || undefined;

  if (effectiveApp) {
    Sentry.setTag("app", effectiveApp);
    Sentry.setTag("app_name", effectiveApp);
  }

  if (cachedEnvironment) {
    Sentry.setTag("environment", cachedEnvironment);
  }

  if (cachedRelease) {
    Sentry.setTag("version", cachedRelease);
  }

  if (resolvedRegion) {
    Sentry.setTag("region", resolvedRegion);
  }

  if (tenantSlug || tenantId) {
    Sentry.setTag("tenant", tenantSlug || String(tenantId));
    Sentry.setContext("tenant", {
      id: tenantId ?? null,
      slug: tenantSlug ?? null,
      name: tenantName ?? null,
    });
  } else {
    Sentry.setTag("tenant", "unknown");
    Sentry.setContext("tenant", null);
  }

  if (memberRole) {
    Sentry.setTag("role", memberRole);
  }

  if (clerkId) {
    Sentry.setContext("auth", { clerk_id: clerkId });
  }

  if (sessionId) {
    Sentry.setContext("session", { id: sessionId });
  }

  if (user) {
    const safeUser = {
      id: user.id ? String(user.id) : undefined,
      email: user.email || undefined,
      username: user.displayName || undefined,
    };
    Sentry.setUser(safeUser);
  } else {
    Sentry.setUser(null);
  }
}

export function clearSentryUserContext({ appName } = {}) {
  if (!sentryEnabled) return;

  const effectiveApp = appName || DEFAULT_APP_NAME;
  if (effectiveApp) {
    Sentry.setTag("app", effectiveApp);
    Sentry.setTag("app_name", effectiveApp);
  }

  Sentry.setUser(null);
  Sentry.setContext("tenant", null);
  Sentry.setContext("auth", null);
  Sentry.setContext("session", null);
  Sentry.setTag("tenant", "unknown");
  Sentry.setTag("role", "unauthenticated");
}
